import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { Card, Modal, Button } from "react-bootstrap";
import axios from "axios";
import { Line } from 'react-chartjs-2';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const Manage_Forecast_Metrics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGraphModal, setShowGraphModal] = useState(false);

  const branchId = localStorage.getItem('branch_id'); 
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (!branchId) return;

    const fetchKPI = async () => {
      try {
        const response = await axios.get(
          `${baseURL}/kpi-branch-targets`,
          { params: { branch_id: branchId } }
        );
        setData(response.data.data);
      } catch (error) {
        console.error("Error fetching KPI data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKPI();
  }, [branchId, baseURL]);

  const renderInlineCardData = (dataArray, bgColor) => (
    <div className="d-flex justify-content-between gap-2 flex-wrap">
      {dataArray.map((item, index) => (
        <div
          key={index}
          className="d-flex flex-column align-items-center border rounded"
          style={{
            flex: 1,
            padding: "0.75rem",
            backgroundColor: bgColor,
            borderColor: "#0d6efd",
            borderWidth: "1px",
            borderStyle: "solid",
          }}
        >
          <h2 className="fw-bold mb-1">{item.value}</h2>
          <h4 className="fw-semibold text-black text-center mb-0">{item.title}</h4>
        </div>
      ))}
    </div>
  );

  const getGraphData = () => {
    const monthStart = new Date(data.target.month + "-01");
    const monthEnd = new Date(data.target.month + "-01");
    monthEnd.setMonth(monthEnd.getMonth() + 1);
    monthEnd.setDate(monthEnd.getDate() - 1);

    const totalDays = Math.ceil((monthEnd - monthStart) / (1000 * 60 * 60 * 24)) + 1;
    const today = new Date(data.current_status.month_current_date);

    const targetAmount = Math.round(data.target.target_amount);
    const receivedAmount = Math.round(data.current_status.received_amount);
    const expectingAmount = Math.round(data.expecting_status.expecting_amount);

    let labels = [];
    let targetLine = [];
    let receivedLine = [];
    let expectedLine = [];

    for (let i = 1; i <= totalDays; i++) {
      labels.push(i);
      const currentDate = new Date(monthStart);
      currentDate.setDate(currentDate.getDate() + (i - 1));
      targetLine.push(Math.round((targetAmount / totalDays) * i));

      if (currentDate <= today) {
        receivedLine.push(Math.round((receivedAmount / today.getDate()) * i));
        expectedLine.push(null);
      } else {
        receivedLine.push(null);
        expectedLine.push(
          Math.round(((expectingAmount - receivedAmount) / (totalDays - today.getDate())) * (i - today.getDate()))
        );
      }
    }

    return {
      labels,
      datasets: [
        { label: 'Target', data: targetLine, borderColor: 'blue', borderWidth: 2, fill: false, tension: 0.4 },
        { label: 'Received', data: receivedLine, borderColor: 'green', borderWidth: 2, fill: false, tension: 0.4 },
        { label: 'Expected', data: expectedLine, borderColor: 'red', borderWidth: 2, fill: false, tension: 0.4 },
      ],
    };
  };

  if (!data && !loading) {
    return <p className="text-danger mt-3">No data available for this branch.</p>;
  }

  const targetData = data && [
    {
      title: "Target Value",
      value: new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(data.target.target_amount)
    },
    { title: "Pre-Customers", value: data.target.pre_customers },
    { title: "Post-Customers", value: data.target.post_customers },
  ];

  const expectingData = data && [
    {
      title: "Expecting Target",
      value: `₹${Number(data.expecting_status.expecting_amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`
    },
    { title: "Pre-Customers", value: Math.round(data.expecting_status.expecting_pre_customers) },
    { title: "Post-Customers", value: Math.round(data.expecting_status.expecting_post_customers) },
  ];

  return (
    <>
      {/* Page Header */}
      <div className="d-flex align-items-center justify-content-between page-titles flex-wrap px-0 py-1 mx-0">
        <div className="mb-2">
          {loading ? (
            <>
              <h4 className="text-primary fw-bold mb-1">
                <Skeleton width={200} height={28} />
              </h4>
              <ol className="breadcrumb fs-7 mb-0">
                <li className="breadcrumb-item"><Skeleton width={100} /></li>
                <li className="breadcrumb-item"><Skeleton width={60} /></li>
                <li className="breadcrumb-item active"><Skeleton width={120} /></li>
              </ol>
            </>
          ) : (
            <>
              <h4 className="text-primary fw-bold mb-1">Manage Forecast Metrics</h4>
              <ol className="breadcrumb fs-7 mb-0">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><i className="fa-solid fa-angles-right fs-8"></i></li>
                <li className="breadcrumb-item active">Metrics Management</li>
              </ol>
            </>
          )}
        </div>

        <div className="mb-2">
          {loading ? (
            <Skeleton width={120} height={35} borderRadius={8} />
          ) : (
            <Button variant="outline-primary" size="sm" onClick={() => setShowGraphModal(true)}>
              <FontAwesomeIcon icon={faChartLine} /> View Graph
            </Button>
          )}
        </div>
      </div>

      {/* Cards Section */}
      <div className="d-flex flex-column mt-3" style={{ gap: '1px' }}>
        {/* Target */}
        <Card className="shadow-sm border-0">
          <Card.Body style={{ padding: '0.75rem' }}>
            {loading ? (
              <>
                <Skeleton width={250} height={25} className="mb-3" />
                <Skeleton height={70} count={3} />
              </>
            ) : (
              <>
                <Card.Title className="fw-semibold text-black mb-2" style={{ fontSize: "1.5rem" }}>
                  Target - ({new Date(data.target.month + "-01").toLocaleString('default', { month: 'long', year: 'numeric' })})
                </Card.Title>
                {renderInlineCardData(targetData, "#d4edda")}
              </>
            )}
          </Card.Body>
        </Card>

        {/* Current Status */}
        <Card className="shadow-sm border-0">
          <Card.Body style={{ padding: '0.75rem' }}>
            {loading ? (
              <>
                <Skeleton width={250} height={25} className="mb-3" />
                <div className="d-flex justify-content-between gap-2">
                  <Skeleton height={120} width="32%" />
                  <Skeleton height={120} width="32%" />
                  <Skeleton height={120} width="32%" />
                </div>
              </>
            ) : (
              <>
                <Card.Title className="fw-semibold text-black mb-3" style={{ fontSize: "1.5rem" }}>
                  Current Status - ({data.current_status.day_count})
                </Card.Title>
                <div className="d-flex justify-content-between gap-2">
                  <div className="d-flex flex-column align-items-center p-3 border rounded"
                    style={{ flex: 1, backgroundColor: "#cfe2ff", borderColor: "#0d6efd", borderWidth: "1px", borderStyle: "solid" }}>
                    <h2 className="fw-bold mt-2">₹{Number(data.current_status.received_amount).toLocaleString('en-IN')}</h2>
                    <span className="text-black">Avg/Day: ₹{Number(data.current_status.average_amount_per_day).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <h4 className="fw-semibold text-center text-black">Target Archived</h4>
                  </div>
                  <div className="d-flex flex-column align-items-center p-3 border rounded"
                    style={{ flex: 1, backgroundColor: "#cfe2ff", borderColor: "#0d6efd", borderWidth: "1px", borderStyle: "solid" }}>
                    <h2 className="fw-bold mt-2">{Math.round(data.current_status.received_pre_customers).toLocaleString()}</h2>
                    <span className="text-black">Avg/Day: {Math.round(data.current_status.average_pre_customers_per_day).toLocaleString()}</span>
                    <h4 className="fw-semibold text-center text-black">Pre-Customers</h4>
                  </div>
                  <div className="d-flex flex-column align-items-center p-3 border rounded"
                    style={{ flex: 1, backgroundColor: "#cfe2ff", borderColor: "#0d6efd", borderWidth: "1px", borderStyle: "solid" }}>
                    <h2 className="fw-bold mt-2">{Math.round(data.current_status.received_post_customers).toLocaleString()}</h2>
                    <span className="text-black">Avg/Day: {Math.round(data.current_status.average_post_customers_per_day).toLocaleString()}</span>
                    <h4 className="fw-semibold text-center text-black">Post-Customers</h4>
                  </div>
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        {/* Expecting */}
        <Card className="shadow-sm border-0">
          <Card.Body style={{ padding: '0.75rem' }}>
            {loading ? (
              <>
                <Skeleton width={220} height={25} className="mb-3" />
                <Skeleton height={70} count={3} />
              </>
            ) : (
              <>
                <Card.Title className="fw-semibold text-black mb-2" style={{ fontSize: "1.5rem" }}>
                  Expecting - ({data.expecting_status.month_end_date})
                </Card.Title>
                {renderInlineCardData(expectingData, "#f8d7da")}
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Graph Modal */}
      {!loading && (
        <Modal show={showGraphModal} onHide={() => setShowGraphModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Monthly Forecast Graph</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Line
              data={getGraphData()}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top" },
                  title: { display: true, text: "Target vs Received vs Expected" },
                },
                scales: {
                  x: { title: { display: true, text: "Day of Month" } },
                  y: { title: { display: true, text: "Amount (INR)" }, beginAtZero: true },
                },
              }}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default Manage_Forecast_Metrics;
